import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes a phone number by removing all non-digit characters except for the leading +
 * @param phone - The phone number to normalize
 * @returns The normalized phone number
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return "";

  // Remove all spaces, dashes, dots, parentheses, and other non-digit characters
  // but preserve the leading + if present
  const hasPlus = phone.trim().startsWith("+");
  const digitsOnly = phone.replace(/\D/g, "");

  return hasPlus ? `+${digitsOnly}` : digitsOnly;
}

/**
 * Validates a phone number after normalizing it
 * Accepts various formats and normalizes before validation
 * @param phone - The phone number to validate
 * @returns True if the phone number is valid
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone) return false;

  const normalized = normalizePhoneNumber(phone);

  // Check for valid phone number patterns:
  // - Optional + followed by country code and number
  // - Must have at least 7 digits (minimum for most phone numbers)
  // - Maximum 15 digits (ITU-T E.164 standard)
  const phoneRegex = /^\+?[1-9]\d{6,14}$/;

  // Additional check: if it starts with +, ensure it has country code
  if (normalized.startsWith("+")) {
    return phoneRegex.test(normalized) && normalized.length >= 8; // +X plus at least 7 digits
  }

  // For numbers without +, ensure they're at least 7 digits and at most 15
  return phoneRegex.test(normalized) && normalized.length >= 7 && normalized.length <= 15;
}

/**
 * Formats a phone number for display in a user-friendly way
 * @param phone - The phone number to format
 * @returns The formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return "";

  const normalized = normalizePhoneNumber(phone);

  // US/Canada format (10 or 11 digits)
  if (normalized.length === 10) {
    return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  } else if (normalized.length === 11 && normalized.startsWith("1")) {
    return `+1 (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7)}`;
  } else if (normalized.startsWith("+1") && normalized.length === 12) {
    // US/Canada international format
    return `+1 (${normalized.slice(2, 5)}) ${normalized.slice(5, 8)}-${normalized.slice(8)}`;
  }

  // For other international numbers, just add basic spacing
  // This is a simple fallback - real world apps might use libraries like libphonenumber
  return normalized;
}
